import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Your enquiry has reached the House of Rudra. We will respond, discreetly.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0D] py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* Left */}
          <div className="md:col-span-5">
            <div className="overline mb-6">Begin a Conversation</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-neutral-50">
              A private <span className="italic text-[#D4AF37]">line</span>,
              <br />
              for a private house.
            </h2>
            <p className="mt-8 text-neutral-400 leading-relaxed max-w-md">
              RUDRA EMPIRE works on invitation and introduction. If you
              represent a Government department, a multinational, or a
              landholder considering a private disposition — we will listen.
            </p>

            <div className="mt-12 space-y-6">
              <a
                data-testid="contact-email-link"
                href="mailto:info@rudraempire.com"
                className="group flex items-start gap-4 link-gold"
              >
                <Mail strokeWidth={1} className="w-5 h-5 text-[#D4AF37] mt-1" />
                <div>
                  <div className="overline mb-1">Correspondence</div>
                  <div className="text-neutral-100">info@rudraempire.com</div>
                </div>
              </a>
            </div>
          </div>

          {/* Right — form */}
          <div className="md:col-span-7 md:col-start-6">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-white/8 bg-[#121215] p-8 md:p-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  id="name"
                  label="Your Name"
                  value={form.name}
                  onChange={update("name")}
                  required
                  testId="input-name"
                />
                <Field
                  id="email"
                  type="email"
                  label="Email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  testId="input-email"
                />
                <Field
                  id="phone"
                  label="Phone (optional)"
                  value={form.phone}
                  onChange={update("phone")}
                  testId="input-phone"
                />
                <Field
                  id="subject"
                  label="Nature of Enquiry"
                  value={form.subject}
                  onChange={update("subject")}
                  testId="input-subject"
                />
              </div>

              <div className="mt-6">
                <label className="overline block mb-3" htmlFor="message">
                  Your Message
                </label>
                <textarea
                  data-testid="input-message"
                  id="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="w-full bg-transparent border-b border-white/15 py-3 text-neutral-100 focus:outline-none focus:border-[#D4AF37] transition-colors resize-none placeholder:text-neutral-600"
                  placeholder="Tell us about the land, the mandate, or the introduction."
                />
              </div>

              <button
                data-testid="submit-inquiry-button"
                type="submit"
                disabled={submitting}
                className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] text-[#08080A] px-8 py-4 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <span>→</span>
                  </>
                )}
              </button>

              <p className="mt-6 text-xs text-neutral-500 leading-relaxed">
                All correspondence is treated with strict confidence. We
                respond within two working days.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label htmlFor={id} className="overline block mb-3">
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
      </label>
      <input
        data-testid={testId}
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-white/15 py-3 text-neutral-100 focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-neutral-600"
      />
    </div>
  );
}
